import * as Dialog from "@radix-ui/react-dialog"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"

interface ChangePasswordModalProps {
    open: boolean
    onClose: () => void
}

export const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
    const { t } = useTranslation()

    const mutation = useMutation({
        mutationFn: () => api.post<{ message: string }>("/auth/change-password", {}),
        onSuccess: () => {
            toast.success(t("admin.changePassword.toastSuccess"))
            onClose()
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.changePassword.toastFailed"))
        },
    })

    return (
        <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
                <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white rounded-[40px] shadow-2xl p-8 outline-none">
                    <div className="flex items-center justify-between mb-6">
                        <Dialog.Title className="text-xl font-black text-slate-900">
                            {t("admin.changePassword.title")}
                        </Dialog.Title>
                        <Dialog.Close className="rounded-2xl p-2 hover:bg-slate-100 transition-colors">
                            <X className="size-5 text-slate-400" />
                        </Dialog.Close>
                    </div>

                    <p className="text-sm text-slate-600 mb-6">
                        {t("admin.changePassword.description")}
                    </p>

                    <div className="flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-bold text-slate-600 bg-slate-100 rounded-2xl hover:bg-slate-200 transition-colors"
                        >
                            {t("admin.changePassword.cancel")}
                        </button>
                        <button
                            type="button"
                            onClick={() => mutation.mutate()}
                            disabled={mutation.isPending}
                            className="px-5 py-2 text-sm font-black text-white bg-brand rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all disabled:opacity-60"
                        >
                            {mutation.isPending
                                ? t("admin.changePassword.sending")
                                : t("admin.changePassword.sendLink")}
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}
